import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import type { DropdownOptionModel } from 'ext-ui/components/Dropdown/components/DropdownOption/DropdownOption'
import SingleDropdown from 'ext-ui/components/Dropdown/SingleDropdown/SingleDropdown'
import Pill, { PillProps } from 'ext-ui/components/Pill/Pill'
import TooltipIcon from 'ext-ui/components/TooltipIcon/TooltipIcon'
import React from 'react'
import { BulkInviteAssignableRole } from 'src/app/BulkInvite/bulkInvite.types'
import * as style from 'src/app/BulkInvite/components/ReviewTable.style'
import { useBulkInviteStrings } from 'src/app/BulkInvite/useBulkInviteStrings'
import { isBulkInviteAssignableRole } from 'src/app/BulkInvite/utils/roles'
import Box from 'src/components/Box/Box'
import DataGridCell from 'src/components/DataGrid/cells/DataGridCell/DataGridCell'
import DataGridCellWrapper from 'src/components/DataGrid/cells/DataGridCellWrapper/DataGridCellWrapper'
import PillCell from 'src/components/DataGrid/cells/PillCell/PillCell'
import UserAvatarCell from 'src/components/DataGrid/cells/UserAvatarCell/UserAvatarCell'
import DataGrid, { DataGridRowHeight } from 'src/components/DataGrid/DataGrid'
import SvgAlertCircled from 'src/components/Svgs/SvgAlertCircled'
import SvgCheckCircled from 'src/components/Svgs/SvgCheckCircled'
import SvgEmail from 'src/components/Svgs/SvgEmail'
import { OrganizationRoles } from 'src/enums'
import { BulkInvitePreviewCategory } from 'src/queries/bulkInvite/bulkInvite.models'

export interface ReviewRowModel {
  id: string
  name: string
  email: string
  role: BulkInviteAssignableRole | null
  phone: string
  category: BulkInvitePreviewCategory | null
  canEditRole: boolean
  roleWarningMessage?: string | null
  /**
   * Backend-supplied reason(s) this row was skipped (SKIPPED_INVALID /
   * SKIPPED_CLASH). Surfaced as subtext under the status pill so admins see the
   * actual problem, not just the generic category label.
   */
  previewErrors: string[]
  /** Per-row send failure reason — only shown in the `showFailure` table. */
  failureReason?: string | null
}

// The DataGrid wrapper subtracts 60px from `height` and we hide the footer, so
// add the column-header height for content to show without an inner scrollbar.
const CHROME_HEIGHT = 116
// Comfy (64) fits the role dropdown and a two-line role/status reason without
// the extra airiness of a fully custom height.
const ROW_HEIGHT = DataGridRowHeight.Comfy

interface ReviewTableProps {
  rows: ReviewRowModel[]
  availableRoles: BulkInviteAssignableRole[]
  onRoleChange: (rowId: string, role: BulkInviteAssignableRole) => void
  /** Show a Failed status pill + failure-reason column (Done step). */
  showFailure?: boolean
  testId?: string
}

type CategoryStyle = {
  color?: PillProps['color']
  /** Leading status icon shown inside the pill; inherits the pill text color. */
  icon: React.FC<React.SVGProps<SVGSVGElement>>
  labelKey: keyof ReturnType<typeof useBulkInviteStrings>
  /**
   * Why-this-matters copy shown in a hover tooltip for informational rows.
   * SKIPPED_INVALID keeps backend `previewErrors` visible because those are
   * short, actionable errors.
   */
  whyKey: keyof ReturnType<typeof useBulkInviteStrings>
}

// Color + text — never color alone (WCAG: status must not rely on color).
const CATEGORY_STYLE: Record<BulkInvitePreviewCategory, CategoryStyle> = {
  VALID_NET_NEW: {
    color: 'highlight',
    icon: SvgEmail,
    labelKey: 'catReady',
    whyKey: 'catReadyWhy',
  },
  SKIPPED_EXISTING_MEMBER: {
    icon: SvgCheckCircled,
    labelKey: 'catExistingMember',
    whyKey: 'catExistingMemberWhy',
  },
  SKIPPED_PENDING_INVITE: {
    icon: SvgCheckCircled,
    labelKey: 'catPendingInvite',
    whyKey: 'catPendingInviteWhy',
  },
  SKIPPED_DUPLICATE_EMAIL: {
    color: 'severe',
    icon: SvgAlertCircled,
    labelKey: 'catConflict',
    whyKey: 'catDuplicateWhy',
  },
  SKIPPED_INVALID: {
    // Missing-data rows are recoverable (fix the file and re-upload), so they
    // read as a warning (amber), not a hard error (red) like conflicts.
    color: 'pending',
    icon: SvgAlertCircled,
    labelKey: 'catInvalid',
    whyKey: 'catInvalidWhy',
  },
  SKIPPED_CLASH: {
    color: 'severe',
    icon: SvgAlertCircled,
    labelKey: 'catConflict',
    whyKey: 'catClashWhy',
  },
  INFO_NOT_IN_FILE: {
    icon: SvgCheckCircled,
    labelKey: 'catNotInFile',
    whyKey: 'catNotInFileWhy',
  },
}

// Categories whose backend `previewErrors` carry the actual, specific reason
// (a bad email, a member conflict, etc.) worth surfacing verbatim as subtext.
const CATEGORIES_WITH_REASON: BulkInvitePreviewCategory[] = [
  'SKIPPED_INVALID',
  'SKIPPED_CLASH',
]

/** First letter of the name (or email) for the avatar fallback. */
const firstInitial = (value: string): string =>
  value.trim().slice(0, 1).toUpperCase()

const ReviewTable: React.FC<ReviewTableProps> = ({
  rows,
  availableRoles,
  onRoleChange,
  showFailure,
  testId,
}) => {
  const strings = useBulkInviteStrings()

  const roleOptions: DropdownOptionModel[] = availableRoles.map((role) => ({
    value: role,
    label: strings.roleLabel(role),
    sublabel:
      role === OrganizationRoles.bookkeeper
        ? strings.roleBookkeeperFullVisibility
        : undefined,
  }))

  const renderName = (
    params: GridRenderCellParams<ReviewRowModel>,
  ): React.ReactElement => {
    const { name, email } = params.row
    const hasFullName = Boolean(name && name !== strings.emptyValue)
    // Email now lives in its own column, so the name cell shows the full name
    // only (or an em dash for email-only rows). The avatar initial falls back
    // to the email when there's no name.
    const label = hasFullName ? name : strings.emptyValue
    return (
      <UserAvatarCell
        params={params}
        user={{
          id: `bulk-invite-${params.row.id}`,
          fullNameOrEmail: label,
          initials: firstInitial(hasFullName ? name : email),
          hasFullName: true,
        }}
        bold
      />
    )
  }

  const renderRole = (
    params: GridRenderCellParams<ReviewRowModel>,
  ): React.ReactElement => {
    const { canEditRole, role, roleWarningMessage, name, email } = params.row

    if (!canEditRole || !role) {
      return <DataGridCell params={params} text={strings.emptyValue} />
    }

    const personLabel = name && name !== strings.emptyValue ? name : email

    return (
      <DataGridCellWrapper params={params}>
        <div
          css={style.roleCell}
          role="group"
          aria-label={strings.roleDropdownAriaLabel(personLabel)}
        >
          <div css={style.roleDropdown}>
            <SingleDropdown
              variant="small"
              data-testid={`bulkInviteRole-${params.row.id}`}
              options={roleOptions}
              noneLabel=""
              clearable={false}
              placeholder={strings.colRole}
              dropdownContainer={document.body}
              selectedValue={{
                value: role,
                label: strings.roleLabel(role),
              }}
              onSelect={(selected): void => {
                if (isBulkInviteAssignableRole(selected.value)) {
                  onRoleChange(params.row.id, selected.value)
                }
              }}
            />
          </div>
          {/* A CSV role that didn't map cleanly still sends as the defaulted
              role; surface the why as a hover tooltip beside the dropdown
              rather than a cramped second line under it. The visually-hidden
              copy keeps the message in the accessibility tree, since the
              tooltip only reveals it on hover. */}
          {roleWarningMessage && (
            <>
              <TooltipIcon
                placement="top"
                iconTestId={`bulkInviteRoleWarning-${params.row.id}`}
              >
                {roleWarningMessage}
              </TooltipIcon>
              <span css={style.visuallyHidden}>{roleWarningMessage}</span>
            </>
          )}
        </div>
      </DataGridCellWrapper>
    )
  }

  const renderStatus = (
    params: GridRenderCellParams<ReviewRowModel>,
  ): React.ReactElement | null => {
    const { category, previewErrors } = params.row
    if (!category) return null
    const categoryStyle = CATEGORY_STYLE[category]
    const why = strings[categoryStyle.whyKey] as string
    // Invalid/clash rows carry the backend's specific reason(s); prefer those
    // verbatim over the generic why-copy so admins can act on the real problem.
    const hasBackendReason =
      CATEGORIES_WITH_REASON.includes(category) && previewErrors.length > 0
    const reason = hasBackendReason ? previewErrors.join(' ') : why
    const showVisibleReason = category === 'SKIPPED_INVALID'
    const label = strings[categoryStyle.labelKey] as string
    const StatusIcon = categoryStyle.icon

    return (
      <DataGridCellWrapper params={params}>
        <div css={style.statusCell}>
          <span css={style.statusWithTooltip}>
            <Pill
              className={style.staticPillClassName}
              color={categoryStyle.color}
              size="medium"
            >
              <span css={style.pillContent}>
                <StatusIcon
                  fill="currentColor"
                  width={16}
                  height={16}
                  viewBox="0 0 24 24"
                />
                {label}
              </span>
            </Pill>
            {/* Ready rows are self-explanatory; invalid rows show the specific
                reason inline below, so only the remaining skipped categories
                need a hover explanation. */}
            {category !== 'VALID_NET_NEW' && !showVisibleReason && (
              <TooltipIcon placement="top">{reason}</TooltipIcon>
            )}
          </span>
          {showVisibleReason && <div css={style.statusReason}>{reason}</div>}
        </div>
      </DataGridCellWrapper>
    )
  }

  const renderFailureStatus = (
    params: GridRenderCellParams<ReviewRowModel>,
  ): React.ReactElement => (
    <PillCell
      className={style.staticPillClassName}
      color="severe"
      params={params}
      size="medium"
    >
      {strings.sendFailed}
    </PillCell>
  )

  const columns: GridColDef<ReviewRowModel>[] = [
    {
      field: 'name',
      headerName: strings.colFullName,
      flex: 1.3,
      minWidth: 200,
      sortable: false,
      renderCell: renderName,
    },
    {
      field: 'email',
      headerName: strings.colEmail,
      flex: 1.4,
      minWidth: 220,
      sortable: false,
    },
    {
      field: 'phone',
      headerName: strings.colPhone,
      flex: 0.9,
      minWidth: 150,
      sortable: false,
    },
    {
      field: 'role',
      headerName: strings.colRole,
      flex: 1.15,
      minWidth: 220,
      sortable: false,
      // Use the localized role label so the cell value (a11y name, copy/export,
      // any filtering) matches the rendered dropdown in every locale.
      valueGetter: (_value, row: ReviewRowModel) =>
        row.role ? strings.roleLabel(row.role) : '',
      renderCell: renderRole,
    },
    {
      field: 'status',
      headerName: strings.colStatus,
      flex: 1.15,
      minWidth: 190,
      sortable: false,
      renderCell: renderStatus,
    },
    ...(showFailure
      ? [
          {
            field: 'failureStatus',
            headerName: strings.colStatus,
            flex: 0.8,
            minWidth: 120,
            sortable: false,
            renderCell: renderFailureStatus,
          } as GridColDef<ReviewRowModel>,
          {
            field: 'failureReason',
            headerName: strings.colFailureReason,
            flex: 1.4,
            minWidth: 200,
            sortable: false,
            valueGetter: (_value, row: ReviewRowModel) =>
              row.failureReason ?? '',
          } as GridColDef<ReviewRowModel>,
        ]
      : []),
  ]

  return (
    <Box noPadding noShadow outlineBorder css={style.gridClip}>
      <DataGrid
        rows={rows}
        columns={columns}
        filtersBody={{}}
        height={CHROME_HEIGHT + rows.length * ROW_HEIGHT}
        rowHeight={ROW_HEIGHT}
        hideFooter
        disableColumnFilter
        disableColumnMenu
        data-testid={testId}
      />
    </Box>
  )
}

export default ReviewTable
